import { useState } from "react"

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
  FormField,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SettingRow,
  Switch,
} from "@coval/ui"
import type { Editor } from "@tiptap/react"
import { ImagePlus } from "lucide-react"

import { notebookMediaUrl, notebookVideoUrl } from "../utils/notebookMedia"

export function NotebookMediaDialog({
  editor,
  disabled,
}: {
  editor: Editor | null
  disabled: boolean
}) {
  const [open, setOpen] = useState(false)
  const [kind, setKind] = useState("image")
  const [src, setSrc] = useState("")
  const [title, setTitle] = useState("")
  const [poster, setPoster] = useState("")
  const [captions, setCaptions] = useState("")
  const [loop, setLoop] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const video = kind === "video"
  const validUrl = (value: string) => !!notebookMediaUrl(value.trim())
  const validSource = !!(video ? notebookVideoUrl : notebookMediaUrl)(
    src.trim(),
  )
  const invalid =
    !validSource ||
    !title.trim() ||
    (video &&
      ((!!poster && !validUrl(poster)) || (!!captions && !validUrl(captions))))
  const unavailable =
    disabled || !editor || editor.isDestroyed || !editor.isEditable
  return (
    <Dialog
      open={open && !unavailable}
      onOpenChange={(value) => {
        setOpen(value)
        if (value) {
          setSubmitted(false)
          setSrc("")
          setTitle("")
          setPoster("")
          setCaptions("")
          setLoop(false)
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="ghost" disabled={unavailable}>
          <ImagePlus />
          Add media
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90dvh] overflow-y-auto p-6" size="md">
        <DialogTitle>Add media</DialogTitle>
        <DialogDescription>
          Use a public HTTPS image, GIF, or direct MP4/WebM video link. Videos
          play with controls; GIFs keep their animation.
        </DialogDescription>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault()
            setSubmitted(true)
            if (invalid || unavailable) {
              return
            }
            const position = editor.state.selection.to
            editor
              .chain()
              .focus()
              .insertContentAt(position, {
                type: kind,
                attrs: video
                  ? {
                      src: src.trim(),
                      title: title.trim(),
                      width: 100,
                      loop,
                      ...(poster.trim() ? { poster: poster.trim() } : {}),
                      ...(captions.trim() ? { captions: captions.trim() } : {}),
                    }
                  : { src: src.trim(), alt: title.trim(), width: 100 },
              })
              .run()
            setOpen(false)
          }}
        >
          <SettingRow label="Media type" stacked>
            <Select value={kind} onValueChange={setKind} disabled={unavailable}>
              <SelectTrigger aria-label="Media type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="image">Image or GIF</SelectItem>
                <SelectItem value="video">Video</SelectItem>
              </SelectContent>
            </Select>
          </SettingRow>
          <FormField
            label="Media URL"
            error={
              submitted && !validSource
                ? video
                  ? "Use a direct HTTPS video link, not a YouTube or Vimeo page."
                  : "Enter a public HTTPS URL without credentials or a local address."
                : undefined
            }
          >
            {(ids) => (
              <Input
                {...ids}
                value={src}
                onChange={(event) => setSrc(event.target.value)}
                maxLength={2000}
                placeholder="https://…"
                disabled={unavailable}
              />
            )}
          </FormField>
          <FormField
            label={video ? "Video description" : "Image description"}
            error={
              submitted && !title.trim()
                ? "Describe the media for readers using assistive technology."
                : undefined
            }
          >
            {(ids) => (
              <Input
                {...ids}
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                maxLength={1000}
                disabled={unavailable}
              />
            )}
          </FormField>
          {video && (
            <>
              <FormField
                label="Preview image URL"
                optional
                error={
                  submitted && !!poster && !validUrl(poster)
                    ? "Enter a full HTTPS image URL."
                    : undefined
                }
              >
                {(ids) => (
                  <Input
                    {...ids}
                    value={poster}
                    onChange={(event) => setPoster(event.target.value)}
                    maxLength={2000}
                    disabled={unavailable}
                  />
                )}
              </FormField>
              <FormField
                label="English captions URL (.vtt)"
                optional
                error={
                  submitted && !!captions && !validUrl(captions)
                    ? "Enter a full HTTPS captions URL."
                    : undefined
                }
              >
                {(ids) => (
                  <Input
                    {...ids}
                    value={captions}
                    onChange={(event) => setCaptions(event.target.value)}
                    maxLength={2000}
                    disabled={unavailable}
                  />
                )}
              </FormField>
              <p className="text-sm">
                Captions need both the video and caption hosts to allow
                cross-origin access.
              </p>
              <SettingRow label="Loop video">
                <Switch
                  checked={loop}
                  onCheckedChange={setLoop}
                  aria-label="Loop video"
                  disabled={unavailable}
                />
              </SettingRow>
            </>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={unavailable}>
              Insert media
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
